import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, UserPlus, ChevronRight } from "lucide-react";
import { PortalPage, Btn, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { inputCls } from "@/components/AuthShell";
import { api } from "@/lib/api";

const EMPTY = { name: "", email: "", role: "", location: "" };

export default function LsoEmployees() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => api("/lso/employees").then(setList).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return setErr("Enter the employee's name.");
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || undefined]));
      await api("/lso/employees", { method: "POST", body });
      setForm(EMPTY);
      await load();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Remove ${emp.name}? Their LSO progress will be removed from your list.`)) return;
    try { await api(`/lso/employees/${emp.id}`, { method: "DELETE" }); await load(); } catch (e) { setErr(e.message); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <PortalPage eyebrow="LASER SAFETY TRAINING" title="Employee Training" back="/lso" backLabel="Course hub" testId="lso-employees-page">
      <Card>
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-2" data-testid="lso-add-employee-form">
          <input data-testid="lso-emp-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
          <input data-testid="lso-emp-email" className={inputCls} placeholder="Email (optional)" type="email" value={form.email} onChange={set("email")} />
          <input data-testid="lso-emp-role" className={inputCls} placeholder="Role (e.g. Operator)" value={form.role} onChange={set("role")} />
          <input data-testid="lso-emp-location" className={inputCls} placeholder="Location (optional)" value={form.location} onChange={set("location")} />
          <div className="sm:col-span-2"><Btn type="submit" busy={busy} data-testid="lso-emp-add"><UserPlus size={16} /> Add Employee</Btn></div>
        </form>
      </Card>
      <ErrorNote>{err}</ErrorNote>
      {!list ? <Spinner /> : list.length === 0 ? (
        <p className="text-sm text-neutral-400" data-testid="lso-emp-empty">No employees yet. Add one above to start their own LSO enrollment.</p>
      ) : list.map((emp) => (
        <div key={emp.id} className="flex items-center gap-3 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4" data-testid={`lso-emp-row-${emp.id}`}>
          <button className="flex flex-1 items-center gap-3 text-left" onClick={() => navigate(`/lso?as=${emp.id}`)} data-testid={`lso-emp-open-${emp.id}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424] font-bold text-[var(--accent)]">{(emp.name || "?")[0].toUpperCase()}</span>
            <span className="flex-1">
              <span className="block font-semibold">{emp.name}</span>
              <span className="text-xs text-neutral-400">{[emp.role || "Operator", emp.email, `${emp.modules_passed || 0} modules passed`, emp.has_certificate && `Cert ${emp.certificate_number}`].filter(Boolean).join(" • ")}</span>
            </span>
            <ChevronRight size={18} className="text-neutral-500" />
          </button>
          <button onClick={() => remove(emp)} aria-label={`Remove ${emp.name}`} data-testid={`lso-emp-delete-${emp.id}`} className="rounded-lg p-2 text-neutral-500 hover:text-[#FF3333]"><Trash2 size={18} /></button>
        </div>
      ))}
    </PortalPage>
  );
}
